import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Download, CheckCircle2, Instagram, Copy,
  ImageIcon, Sparkles, ArrowLeft, Share2, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

/**
 * Admin — daily Instagram post drafts.
 *
 * Shows today's auto-generated carousel + story image. The admin can
 * regenerate, copy the suggested caption, download all assets in one
 * tap (mobile native share intent when possible), and mark the draft
 * as posted manually. The "Publish to IG" button is wired but will
 * return a friendly 503 until Meta App Review approves us.
 */
export default function AdminIGPostsPage() {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState('');
  const [previewIdx, setPreviewIdx] = useState(0);

  const fetchDraft = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ig-posts/today`, { headers: authHeaders() });
      setDraft(res.data);
      setCaption(res.data?.caption_suggestion || '');
      setPreviewIdx(0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load today's draft");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDraft(); }, [fetchDraft]);

  const regenerate = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/ig-posts/generate-today`, {}, { headers: authHeaders() });
      setDraft(res.data);
      setCaption(res.data?.caption_suggestion || '');
      setPreviewIdx(0);
      toast.success('Draft regenerated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Regenerate failed');
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success('Caption copied — paste into IG');
    } catch {
      toast.error('Copy failed');
    }
  };

  // In-app "save-viewer" modal: shows the image inline, user long-presses
  // to save. This is the ONLY method that works reliably across every
  // mobile PWA / TWA. We no longer use `window.open('_blank')` — on
  // Android TWA it opens a Chrome Custom Tab overlay that some users
  // can't dismiss without force-quitting the app.
  const [saveViewer, setSaveViewer] = useState({ open: false, urls: [], idx: 0 });
  const openSaveViewer = (urls, startIdx = 0) => {
    if (!urls || urls.length === 0) return;
    setSaveViewer({ open: true, urls, idx: startIdx });
  };
  const closeSaveViewer = () => setSaveViewer((s) => ({ ...s, open: false }));

  const downloadAll = () => {
    if (!draft) return;
    const urls = [
      ...(draft.carousel_image_urls || []),
      ...(draft.story_image_url ? [draft.story_image_url] : []),
    ];
    if (urls.length === 0) {
      toast.error('Nothing to save yet — tap Regenerate first');
      return;
    }
    openSaveViewer(urls, 0);
  };

  const saveCurrentPreview = () => {
    // Save just the currently-previewed slide.
    if (!draft) return;
    const url = isStoryPreview
      ? draft.story_image_url
      : draft.carousel_image_urls?.[previewIdx];
    if (!url) return;
    openSaveViewer([url], 0);
  };

  const nativeShare = async () => {
    if (!draft) return;
    if (!navigator.share) {
      toast.message(
        'Sharing not supported here — tap Save to camera roll instead, then upload from IG',
        { duration: 6000 },
      );
      return;
    }
    setBusy(true);
    try {
      const fullImageUrl = process.env.REACT_APP_BACKEND_URL + draft.carousel_image_urls[0];

      // Preferred: share the file directly (works on most desktop
      // browsers + some mobile). Falls back to URL share when the
      // browser rejects the file payload — that path works on iOS/Android
      // and lets Instagram pick up the image via its "Open with" chooser.
      try {
        const res = await axios.get(fullImageUrl, { responseType: 'blob' });
        const file = new File([res.data], 'rollm8s.jpg', { type: 'image/jpeg' });
        const filePayload = { files: [file], text: caption, title: 'New events on RollM8s' };
        if (navigator.canShare && navigator.canShare(filePayload)) {
          await navigator.share(filePayload);
          return;
        }
      } catch {
        // fall through to URL share
      }

      // URL fallback — always supported when navigator.share exists.
      await navigator.share({
        title: 'New events on RollM8s',
        text: caption,
        url: fullImageUrl,
      });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error('Share cancelled or blocked — try Save to camera roll');
      }
    } finally {
      setBusy(false);
    }
  };

  const publishToIG = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ig-posts/drafts/${draft.draft_id}/publish`, {}, { headers: authHeaders() });
      toast.success('Posted to Instagram');
      fetchDraft();
    } catch (err) {
      toast.message(err?.response?.data?.detail || 'Publish endpoint not ready', { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const markPosted = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ig-posts/drafts/${draft.draft_id}/mark-posted`, {}, { headers: authHeaders() });
      toast.success('Marked as posted');
      fetchDraft();
    } catch (err) {
      toast.error('Mark failed');
    } finally {
      setBusy(false);
    }
  };

  const eventCount = draft?.event_ids?.length || 0;
  const carouselUrls = draft?.carousel_image_urls || [];
  const storyUrl = draft?.story_image_url;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const previewSrc = previewIdx < carouselUrls.length
    ? apiBase + carouselUrls[previewIdx]
    : storyUrl ? apiBase + storyUrl : null;
  const isStoryPreview = previewIdx === carouselUrls.length;
  const publishStatus = draft?.publish_status || 'draft';

  return (
    <div className="min-h-screen bg-background text-foreground pb-24" data-testid="admin-ig-posts-page">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border/40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin" className="rounded-full p-2 hover:bg-muted/40">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Instagram className="w-5 h-5 text-pink-400" />
              IG Daily Drafts
            </h1>
            <p className="text-xs text-muted-foreground">Auto-built at 17:00 UK — @rollm8s</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={regenerate}
            disabled={busy}
            data-testid="ig-regen-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Regenerate
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && draft && (
          <>
            <Card className="border-pink-400/30 bg-gradient-to-br from-pink-500/8 to-cyan-500/4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between gap-2 text-base">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-400" />
                    Today&rsquo;s draft · {draft.for_date}
                  </span>
                  <Badge
                    className={
                      publishStatus === 'posted_manually'
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                        : publishStatus === 'posted'
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                          : 'bg-amber-500/20 text-amber-300 border-amber-400/40'
                    }
                    data-testid="ig-publish-status-pill"
                  >
                    {publishStatus.replace('_', ' ')}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  <span className="font-bold text-foreground">{eventCount}</span>{' '}
                  event{eventCount === 1 ? '' : 's'} bundled today.
                  {eventCount === 0 && ' — carousel will read “0 events live”. Regenerate after listing one.'}
                </p>
              </CardContent>
            </Card>

            {/* Preview viewer */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="ig-preview-thumb-rail">
                  {carouselUrls.map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPreviewIdx(i)}
                      className={`relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                        previewIdx === i ? 'border-pink-400 ring-2 ring-pink-400/30' : 'border-border/30'
                      }`}
                      data-testid={`ig-thumb-${i}`}
                    >
                      <img src={apiBase + url} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 left-0 right-0 text-[9px] font-bold text-center bg-black/60 text-white py-0.5">
                        {i === 0 ? 'COVER' : i}
                      </span>
                    </button>
                  ))}
                  {storyUrl && (
                    <button
                      type="button"
                      onClick={() => setPreviewIdx(carouselUrls.length)}
                      className={`relative flex-shrink-0 w-12 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                        previewIdx === carouselUrls.length ? 'border-pink-400 ring-2 ring-pink-400/30' : 'border-border/30'
                      }`}
                      data-testid="ig-thumb-story"
                    >
                      <img src={apiBase + storyUrl} alt="Story" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 left-0 right-0 text-[9px] font-bold text-center bg-black/60 text-white py-0.5">
                        STORY
                      </span>
                    </button>
                  )}
                </div>

                {previewSrc && (
                  <div
                    className={`rounded-xl overflow-hidden bg-black flex items-center justify-center mx-auto ${
                      isStoryPreview ? 'max-w-[260px]' : 'max-w-md'
                    }`}
                    data-testid="ig-preview-frame"
                  >
                    <img
                      src={previewSrc}
                      alt={isStoryPreview ? 'Story preview' : `Carousel slide ${previewIdx + 1}`}
                      className="w-full h-auto"
                    />
                  </div>
                )}

                {/* Save just this slide — clearer than a batch "download
                    all" on mobile because the user sees exactly what
                    they're saving. */}
                {previewSrc && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mx-auto rounded-full gap-2 mt-2"
                    onClick={saveCurrentPreview}
                    data-testid="ig-save-current-btn"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Save this slide to camera roll
                  </Button>
                )}

                {!previewSrc && (
                  <div className="text-center py-12 text-muted-foreground" data-testid="ig-no-preview">
                    <ImageIcon className="w-8 h-8 mx-auto opacity-50 mb-2" />
                    No preview yet — tap Regenerate.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Caption */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Suggested caption</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="rounded-xl min-h-[160px] font-mono text-xs"
                  data-testid="ig-caption-textarea"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-full gap-2"
                  onClick={copyCaption}
                  data-testid="ig-copy-caption-btn"
                >
                  <Copy className="w-4 h-4" /> Copy caption
                </Button>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Post it</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  type="button"
                  className="w-full rounded-full gap-2 h-12"
                  onClick={publishToIG}
                  disabled={busy}
                  data-testid="ig-publish-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />}
                  Publish to IG (auto)
                </Button>
                <p className="text-[11px] text-muted-foreground text-center -mt-1">
                  Auto-publish unlocks once Meta App Review approves <code>instagram_content_publish</code>.
                </p>
                <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/8 px-3 py-2.5 text-[11px] text-cyan-100/90 space-y-1">
                  <p className="font-bold">While auto-post waits on Meta review:</p>
                  <p>1. Tap <span className="font-bold">Save all</span> below → each image shows full-screen.</p>
                  <p>2. Long-press the image → Save to Photos / Download image.</p>
                  <p>3. Tap Next to move through the set.</p>
                  <p>4. Open IG → New post → attach the saved images → paste the caption.</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full gap-2"
                    onClick={nativeShare}
                    disabled={busy}
                    data-testid="ig-native-share-btn"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full gap-2"
                    onClick={downloadAll}
                    disabled={busy}
                    data-testid="ig-download-all-btn"
                  >
                    <Download className="w-4 h-4" /> Save all
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full rounded-full gap-2 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={markPosted}
                  disabled={busy || publishStatus === 'posted_manually'}
                  data-testid="ig-mark-posted-btn"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {publishStatus === 'posted_manually' ? 'Already marked posted' : 'I posted it — mark done'}
                </Button>
              </CardContent>
            </Card>

            {/* Event list */}
            {draft.event_summary && draft.event_summary.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">In this post</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {draft.event_summary.map((ev) => (
                    <div
                      key={ev.event_id}
                      className="text-xs text-muted-foreground px-2 py-1.5 rounded-lg bg-muted/30"
                    >
                      <span className="font-bold text-foreground">{ev.name}</span>
                      {ev.city && <span> — {ev.city}</span>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* ── Save-viewer modal ───────────────────────────────────────
          The reliable path for saving images from a mobile PWA / TWA.
          The image is rendered inline; the user long-presses it →
          Save to Photos (iOS) / Download image (Android). No
          window.open, no Chrome Custom Tab overlay that traps the app. */}
      {saveViewer.open && saveViewer.urls.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          data-testid="ig-save-viewer"
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 flex-shrink-0">
            <p className="text-white text-sm font-bold" data-testid="ig-save-viewer-position">
              {saveViewer.idx + 1} of {saveViewer.urls.length}
            </p>
            <button
              type="button"
              onClick={closeSaveViewer}
              className="rounded-full w-9 h-9 grid place-items-center bg-white/10 hover:bg-white/20 text-white"
              data-testid="ig-save-viewer-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image body */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 py-3 min-h-0 gap-3 overflow-y-auto">
            <p className="text-white/80 text-xs text-center max-w-sm">
              <span className="font-bold text-pink-300">Long-press the image below</span>
              {' → '}
              <span className="opacity-80">Save to Photos</span>{' '}
              <span className="opacity-60">(iOS) / </span>
              <span className="opacity-80">Download image</span>{' '}
              <span className="opacity-60">(Android)</span>
            </p>
            <img
              src={process.env.REACT_APP_BACKEND_URL + saveViewer.urls[saveViewer.idx]}
              alt={`Slide ${saveViewer.idx + 1}`}
              className="max-w-full max-h-[60vh] object-contain rounded-lg"
              draggable={false}
              data-testid="ig-save-viewer-image"
            />
          </div>

          {/* Footer nav */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10 flex-shrink-0">
            <button
              type="button"
              onClick={() => setSaveViewer((s) => ({ ...s, idx: Math.max(0, s.idx - 1) }))}
              disabled={saveViewer.idx === 0}
              className="rounded-full px-4 h-10 grid place-items-center bg-white/10 hover:bg-white/20 text-white disabled:opacity-40 disabled:pointer-events-none gap-2 flex"
              data-testid="ig-save-viewer-prev"
            >
              <ChevronLeft className="w-4 h-4" />
              Prev
            </button>
            {saveViewer.idx < saveViewer.urls.length - 1 ? (
              <button
                type="button"
                onClick={() => setSaveViewer((s) => ({ ...s, idx: Math.min(s.urls.length - 1, s.idx + 1) }))}
                className="rounded-full px-4 h-10 grid place-items-center bg-pink-500 hover:bg-pink-600 text-white gap-2 flex"
                data-testid="ig-save-viewer-next"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={closeSaveViewer}
                className="rounded-full px-5 h-10 grid place-items-center bg-emerald-500 hover:bg-emerald-600 text-white gap-2 flex"
                data-testid="ig-save-viewer-done"
              >
                <CheckCircle2 className="w-4 h-4" />
                Done
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
